import axios from "axios";
import AuthenticationFailureHandler from "../utils/AuthenticationFailureHandler";
import { is401 } from "../errors/ErrorConstants";
import { ONBOARDING_BASE_URL } from "../utils/EmpUtil";

/** React Query key for dealer Stripe details. */
export const DEALER_STRIPE_DETAILS_QUERY_KEY = "dealerStripeDetails";

/**
 * Client for Stripe onboarding APIs (deposit amounts, account linkage, etc.).
 */
export default class StripeOnboardingClient {
  static baseUrl = ONBOARDING_BASE_URL;

  /**
   * Get Stripe account and deposit details for a dealer.
   * @param {string} dealerCommonId
   * @returns {Promise<{
   *   stripeAccountId?: string|null,
   *   status?: string|null,
   *   orderDeposit?: number|string|null,
   *   maxCreditCardAmount?: number|string|null,
   *   maxAchAmount?: number|string|null,
   *   cartCheckoutDeposit?: number|string|null,
   * }|null>}
   */
  static async getStripeDetails(dealerCommonId) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/stripe?dealerCommonId=${encodedDealerCommonId}`;

    try {
      const response = await axios.get(url);
      return response.data ?? null;
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Save Stripe client deposit amounts for a dealer.
   * @param {string} dealerCommonId
   * @param {{
   *   orderDeposit: number,
   *   maxCreditCardAmount: number,
   *   maxAchAmount: number,
   *   cartCheckoutDeposit: number,
   *   updatedBy: string,
   * }} payload
   * @returns {Promise<void>}
   */
  static async saveDepositAmounts(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/stripe/deposit-amounts?dealerCommonId=${encodedDealerCommonId}`;

    try {
      await axios.post(url, payload);
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Connect a found Stripe account ID to the dealer onboarding record.
   * @param {string} dealerCommonId
   * @param {{ stripeAccountId: string, updatedBy: string }} payload
   * @returns {Promise<{
   *   stripeAccountId?: string|null,
   *   status?: string|null,
   *   orderDeposit?: number|string|null,
   *   maxCreditCardAmount?: number|string|null,
   *   maxAchAmount?: number|string|null,
   *   cartCheckoutDeposit?: number|string|null,
   * }>}
   */
  static async connectStripeAccount(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/stripe/connect?dealerCommonId=${encodedDealerCommonId}`;

    try {
      const response = await axios.post(url, payload);
      return response.data ?? null;
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }

  /**
   * Disconnect Stripe as payment processor and clear deposit amounts.
   * @param {string} dealerCommonId
   * @param {{ updatedBy: string }} payload
   * @returns {Promise<{
   *   stripeAccountId?: string|null,
   *   status?: string|null,
   *   orderDeposit?: number|string|null,
   *   maxCreditCardAmount?: number|string|null,
   *   maxAchAmount?: number|string|null,
   *   cartCheckoutDeposit?: number|string|null,
   * }|null>}
   */
  static async disconnectStripePaymentProcessor(dealerCommonId, payload) {
    const encodedDealerCommonId = encodeURIComponent(dealerCommonId);
    const url = `${this.baseUrl}/dealers/stripe/disconnect?dealerCommonId=${encodedDealerCommonId}`;

    try {
      const response = await axios.post(url, payload);
      return response.data ?? null;
    } catch (error) {
      if (is401(error)) AuthenticationFailureHandler.execute();
      throw error;
    }
  }
}
